import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import { usePageMeta } from "@/hooks/usePageMeta";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cashmyproperty.ai/" },
    { "@type": "ListItem", position: 2, name: "Services", item: "https://cashmyproperty.ai/services" },
    { "@type": "ListItem", position: 3, name: "Legal Services", item: "https://cashmyproperty.ai/services/legal" },
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is title diligence and why is it critical?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Title diligence is a 30-year ownership chain audit that verifies every single transaction, inheritance, partition, and court order affecting your property. A single missing link can make the title 'defective' — which means you cannot sell, mortgage, or develop the land until it is resolved. Our legal team traces the complete ownership history and flags every risk before it becomes a deal-breaker.",
      },
    },
    {
      "@type": "Question",
      name: "How long does agricultural-to-residential land conversion take?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "DC conversion (District Commissioner approval) typically takes 3–6 months in Karnataka. The timeline depends on the local planning authority's workload, document completeness, and whether any objections are raised during the public notice period. Our team handles all paperwork, follow-ups, and liaison with revenue departments to minimize delays.",
      },
    },
    {
      "@type": "Question",
      name: "What approvals are needed before I can develop my land?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The approval chain depends on your land type and intended use. Typically: land conversion (if agricultural), khata transfer, building plan sanction from the local body (BBMP/BDA/panchayat), RERA registration (for projects over 500 sq.m), environmental clearance (for large projects), and fire/NOC from relevant departments. We map the complete approval chain for your specific situation.",
      },
    },
    {
      "@type": "Question",
      name: "Do you handle litigation and property disputes?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. We manage property-related litigation including title disputes, partition suits, specific performance suits for agreement-to-sell breaches, encroachment cases, and succession matters. Our panel includes advocates practicing before the Karnataka High Court, civil courts, and RERA tribunals. Initial case assessment is complimentary.",
      },
    },
  ],
};

const steps = [
  {
    step: "01",
    title: "Initial Consultation & Document Audit",
    desc: "We review your existing property documents — sale deeds, ECs, tax receipts, khata certificates, and any prior legal opinions. Within 48 hours, we deliver a preliminary risk assessment identifying red flags and missing documents.",
    icon: "ri-file-search-line",
  },
  {
    step: "02",
    title: "30-Year Title Search",
    desc: "Our legal team traces the complete ownership chain for a minimum of 30 years through sub-registrar records, revenue department archives, and encumbrance certificates. Every transaction, inheritance, and court order is verified and documented in a title opinion report.",
    icon: "ri-search-line",
  },
  {
    step: "03",
    title: "Encumbrance & Litigation Check",
    desc: "We check for mortgages, liens, attachments, pending litigation, acquisition notifications, and government claims on your property. This includes searches across civil court records, RERA tribunals, and revenue department proceedings.",
    icon: "ri-alert-line",
  },
  {
    step: "04",
    title: "Compliance & Approvals Management",
    desc: "Based on your property type and goals, we map the complete approval chain — land conversion, khata transfer, building plan sanction, RERA registration, and environmental/NOC clearances. We handle the paperwork, liaison, and follow-ups.",
    icon: "ri-file-check-line",
  },
  {
    step: "05",
    title: "Legal Opinion & Documentation",
    desc: "You receive a comprehensive legal opinion report with a clear 'marketable title' assessment, risk matrix, and recommended actions. For transactions, we draft and register all required documents — sale deeds, agreements, POAs, and affidavits.",
    icon: "ri-scales-3-line",
  },
];

const services = [
  { icon: "ri-file-search-line", title: "Title Diligence & Verification", desc: "30-year ownership chain audit with encumbrance, litigation, and acquisition risk assessment. Comprehensive title opinion report for buyers, sellers, and lenders." },
  { icon: "ri-government-line", title: "Land Conversion (DC Conversion)", desc: "Agricultural to non-agricultural conversion, industrial to residential/commercial, and change of land use. Complete paperwork and DC office liaison." },
  { icon: "ri-building-line", title: "Building Plan & Development Approvals", desc: "Plan sanction from BBMP, BDA, BMRDA, and local panchayats. RERA project registration. Environmental clearance for large developments." },
  { icon: "ri-file-text-line", title: "Sale Deed & Agreement Drafting", desc: "Sale deeds, agreement to sell, GPA, joint development agreements, lease deeds, and mortgage documents drafted by property law specialists." },
  { icon: "ri-hand-coin-line", title: "Transaction & Registration Support", desc: "End-to-end registration management including stamp duty calculation, e-Khata processing, sub-registrar liaison, and mutation of revenue records post-registration." },
  { icon: "ri-scales-2-line", title: "Litigation & Dispute Resolution", desc: "Title disputes, partition suits, specific performance, encroachment cases, and succession matters. Panel advocates experienced in Karnataka property law." },
];

const faqs = [
  { q: "What is title diligence and why is it critical?", a: "Title diligence is a 30-year ownership chain audit that verifies every single transaction, inheritance, partition, and court order affecting your property. A single missing link can make the title 'defective' — which means you cannot sell, mortgage, or develop the land until it is resolved. Our legal team traces the complete ownership history and flags every risk before it becomes a deal-breaker." },
  { q: "How long does agricultural-to-residential land conversion take?", a: "DC conversion (District Commissioner approval) typically takes 3–6 months in Karnataka. The timeline depends on the local planning authority's workload, document completeness, and whether any objections are raised during the public notice period. Our team handles all paperwork, follow-ups, and liaison with revenue departments to minimize delays." },
  { q: "What approvals are needed before I can develop my land?", a: "The approval chain depends on your land type and intended use. Typically: land conversion (if agricultural), khata transfer, building plan sanction from the local body (BBMP/BDA/panchayat), RERA registration (for projects over 500 sq.m), environmental clearance (for large projects), and fire/NOC from relevant departments. We map the complete approval chain for your specific situation." },
  { q: "Do you handle litigation and property disputes?", a: "Yes. We manage property-related litigation including title disputes, partition suits, specific performance suits for agreement-to-sell breaches, encroachment cases, and succession matters. Our panel includes advocates practicing before the Karnataka High Court, civil courts, and RERA tribunals. Initial case assessment is complimentary." },
];

export default function LegalPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  usePageMeta({
    title: "Legal Services — Title Diligence, Land Conversion & Property Documentation | CashMyProperty.ai",
    description: "End-to-end property legal services: 30-year title diligence, encumbrance checks, land conversion (DC conversion), building approvals, RERA registration, sale deed drafting, and litigation support. Karnataka property law specialists. Protect your land investment with bulletproof legal documentation.",
    canonicalUrl: "https://cashmyproperty.ai/services/legal",
  });

  return (
    <PageLayout>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      {/* Hero */}
      <section className="relative w-full min-h-[480px] md:min-h-[520px] overflow-hidden bg-background-50">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20legal%20office%20with%20property%20documents%2C%20stamp%20papers%2C%20and%20scales%20of%20justice%20on%20wooden%20desk%2C%20warm%20amber%20desk%20lamp%20lighting%2C%20law%20books%20in%20background%2C%20clean%20modern%20aesthetic%2C%20India%20property%20law%2C%20cinematic%20photography%2C%20deep%20teal%20and%20gold%20palette%2C%20high%20detail%2C%20trust%20and%20authority%20mood&width=1800&height=800&seq=service-legal-hero&orientation=landscape"
          alt="Legal Services — CashMyProperty.ai"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-background-50/40" />
        <div className="relative h-full flex items-center container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 mb-5">
              <span className="text-white text-xs font-medium uppercase tracking-widest">Service Detail</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-[1.1] mb-4">
              <span className="text-white [text-shadow:0_0_24px_rgba(0,0,0,0.92)]">Legal</span>
              <br />
              <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-transparent drop-shadow-[0_0_16px_rgba(0,0,0,0.78)]">Services</span>
            </h1>
            <p className="text-lg text-white/90 leading-relaxed max-w-lg [text-shadow:0_0_16px_rgba(0,0,0,0.84)]">
              Title diligence, land conversion, approvals, and documentation — handled by Karnataka property law specialists.
            </p>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="py-4 bg-background-50 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-4-line mr-1" />Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to="/services" className="hover:text-primary-500 transition-colors whitespace-nowrap">Services</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-950 font-medium">Legal</span>
          </nav>
        </div>
      </section>

      {/* What is Legal */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Property Law, Simplified</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
                One Missing Document Can Freeze Your Land for Years
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Indian property law is layered, complex, and varies by state. A single gap in the title chain, an unapproved conversion, or a pending litigation can render your land unsaleable — and you may not discover it until a buyer's lawyer flags it during due diligence.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Our legal team brings <strong className="text-foreground-950">15+ years of Karnataka property law expertise</strong> — from 30-year title searches and DC conversions to RERA registrations and High Court litigation. We do not just identify problems; we resolve them.
              </p>
              <p className="text-foreground-600 leading-relaxed">
                Whether you are buying, selling, developing, or inheriting land — bulletproof legal documentation is not optional. It is the <strong className="text-foreground-950">foundation of every successful transaction</strong>.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Close-up%20of%20property%20documents%2C%20stamp%20papers%2C%20and%20legal%20seals%20on%20wooden%20desk%2C%20professional%20legal%20setting%2C%20warm%20amber%20lighting%2C%20shallow%20depth%20of%20field%2C%20India%20real%20estate%20documentation%2C%20high%20detail%20photography%2C%20teal%20and%20gold%20tones%2C%20trust%20and%20security%20mood&width=800&height=600&seq=legal-documents-01&orientation=landscape"
                alt="Legal Documentation"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Legal Services Grid */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Our Legal Services</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Complete Property Legal Coverage</h2>
            <p className="text-foreground-600">From title search to registration. Every legal service your property needs.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {services.map((s) => (
              <div key={s.title} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4">
                  <i className={`${s.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-2">{s.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Process</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Our 5-Step Legal Process</h2>
            <p className="text-foreground-600">From document audit to registered deed. Systematic and thorough.</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <div key={step.step} className="flex gap-6 pb-10 relative">
                {i < steps.length - 1 && <div className="absolute left-[26px] top-14 bottom-0 w-px bg-primary-900/20" />}
                <div className="w-[52px] h-[52px] flex items-center justify-center rounded-full bg-primary-500/10 border-2 border-primary-500/30 shrink-0 z-10">
                  <span className="text-sm font-display font-bold text-primary-500">{step.step}</span>
                </div>
                <div className="pt-2">
                  <div className="flex items-center gap-3 mb-2">
                    <i className={`${step.icon} text-xl text-primary-500`} />
                    <h3 className="font-display font-semibold text-lg text-foreground-950">{step.title}</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warning Signs */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Red Flags</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Signs Your Property Needs a Legal Audit</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              "Title deed is more than 30 years old and ownership chain is unclear",
              "Property was inherited through multiple generations without formal partition",
              "Land is classified as agricultural but surrounded by developed properties",
              "Khata or property tax records do not match the sale deed description",
              "You have received an acquisition or BDA/BIAPPA notification",
              "A portion of the land is under a road widening or infrastructure project",
              "There is a pending court case involving any previous owner",
              "You are planning to sell, mortgage, or develop the property within 12 months",
            ].map((sign) => (
              <div key={sign} className="flex items-start gap-3 p-4 rounded-xl card-dark">
                <i className="ri-error-warning-line text-primary-500 text-lg shrink-0 mt-0.5" />
                <span className="text-sm text-foreground-800">{sign}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-background-200/50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
            {[
              { value: "15+ Yrs", label: "Property Law Experience" },
              { value: "2,000+", label: "Title Searches Completed" },
              { value: "500+", label: "DC Conversions Processed" },
              { value: "₹0", label: "Initial Consultation Fee" },
            ].map((stat) => (
              <div key={stat.label} className="p-5">
                <div className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-1">{stat.value}</div>
                <div className="text-xs text-foreground-500 font-medium uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold text-foreground-950">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`rounded-xl border transition-all duration-300 ${openFaq === i ? "bg-background-200 border-primary-700/40" : "card-dark"}`}>
                <button className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-display font-semibold text-foreground-950 pr-4">{faq.q}</span>
                  <i className={`ri-add-line text-primary-500 text-xl shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-6 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            Is Your Property Legally Bulletproof?
          </h2>
          <p className="text-foreground-600 mb-8">
            Book a free initial consultation. We will review your documents and identify any red flags — no charge, no commitment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              Book Free Legal Consultation
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/feasibility-report" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-primary-700/50 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap">
              Get AI Report — ₹4,999
              <i className="ri-file-chart-line" />
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}